import React from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import RichText from "common/blocks/RichText";
import specDataHelper from "features/vehicleDetails/helpers/specDataHelper";
import { featuresSpecsModal } from "features/vehicleDetails/modals";
import { FeaturesSpecsSelectedTab } from "features/vehicleDetails/modals/FeaturesSpecs";
import { EventNames, usePub } from "hooks/usePublishSubscribe";
import {
    VehicleSpecsFeaturesPacks,
    VehicleSpecsFeaturesPacksKeys,
} from "resolvers/data/pdp/pdpVehicleSpecsResolver";
import { ResolvedRichText } from "resolvers/data/richTextResolver";
import { toTitleCase } from "utils/language-helpers";

import { OrderedSpec } from "../VehicleDetails/components/CarSpecs";
import styles from "./VehicleFigures.module.scss";

const cx = classNames.bind(styles);

export type VehicleFiguresColContent = {
    colTitle: string;
    buttonLabel: string;
    noDataText: string;
    sideBarOpen: string;
    data: VehicleSpecsFeaturesPacks;
    order: OrderedSpec[];
    maxRows: number;
};

export type VehicleFigureColumn = VehicleFiguresColContent;

export type VehicleFiguresProps = {
    components: {
        mot: VehicleFigureColumn;
        runningCosts: VehicleFigureColumn;
        performance: VehicleFigureColumn;
        warranty: VehicleFigureColumn;
        dimensions: VehicleFigureColumn;
        legal: ResolvedRichText;
        serviceHistory: VehicleFigureColumn;
    };
};

export const VehicleFigures = ({
    components,
    handleOpenFeaturesSpecsModal,
}: VehicleFiguresProps & {
    handleOpenFeaturesSpecsModal: (
        selectedRow?: string,
        selectedTab?: FeaturesSpecsSelectedTab
    ) => any;
}) => {
    const publish = usePub();

    const { performance, runningCosts, warranty, mot, serviceHistory } = components;
    const createRow = (orderedSpec: OrderedSpec, value: string) => (
        <div key={orderedSpec.key} className={cx("row")}>
            <span className={cx("key")}>{orderedSpec.label}</span>
            <span className={cx("value")}>
                {toTitleCase(value)}
                {orderedSpec?.postfix ?? ""}
            </span>
        </div>
    );
    const rawCols = [runningCosts, performance, mot, warranty, serviceHistory]; //MOT & warranty included here to be in the same display container
    const modalRows = ["runningcosts", "performance", "mot", "warranty", "servicehistory"];

    const cols = rawCols.map(
        (
            { buttonLabel = "", colTitle = "", data, order = [], maxRows = 8, sideBarOpen },
            index
        ) => {
            const modalRow = modalRows[index] as VehicleSpecsFeaturesPacksKeys;

            const { validSpecsCount, orderedSpecs } = specDataHelper({
                maxSummarySpecs:
                    modalRow === "servicehistory" ? data.servicehistory.length : maxRows,
                callbackFn: (spec, value) => createRow(spec, value),
                specs: data,
                specsKey: modalRow,
                specsOrder: order,
            });
            let motValidSpecsCount = 0;
            let motOrderedSpecs = null;

            if (modalRow === "warranty") {
                const motSpecs = specDataHelper({
                    maxSummarySpecs: maxRows,
                    callbackFn: (spec, value) => createRow(spec, value),
                    specs: data,
                    specsKey: "mot",
                    specsOrder: order,
                });

                motValidSpecsCount = motSpecs.validSpecsCount;
                motOrderedSpecs = motSpecs.orderedSpecs;
            }

            const lastCol = index + 1 === rawCols.length; // figures out the last col to add seperators
            if (modalRow === "mot") return null; //dont show mot row - included as part of warranty

            const serviceHistoryViewableRecordsLimit = 4; // total number excludes the first one displayed in its own box
            let recentServiceDate = "";
            let recentServiceMileage = "";
            let serviceHistoryRequiresExpansionButton = false;
            let totalRemovedValues = 0;
            if (modalRow === "servicehistory") {
                recentServiceDate = orderedSpecs[0]?.key?.toLocaleString()
                    ? orderedSpecs[0].key?.toLocaleString()
                    : "";
                recentServiceMileage = orderedSpecs[0]?.props.children[1].props.children[0];
                orderedSpecs.shift();
                serviceHistoryRequiresExpansionButton =
                    orderedSpecs.length > serviceHistoryViewableRecordsLimit;

                if (serviceHistoryRequiresExpansionButton) {
                    //remove overhanging entries from UI
                    let originalLength = orderedSpecs.length;
                    orderedSpecs.splice(3); // Show just 3 records in the list if there are more than 5 records in total
                    totalRemovedValues = originalLength - orderedSpecs.length;
                }
            }
            const sidebarParams = handleOpenFeaturesSpecsModal(sideBarOpen, "specs");
            return modalRow === "servicehistory" ? (
                <React.Fragment key={colTitle}>
                    <div className={cx("col")}>
                        <div className={cx("titleRow")}>
                            <h4 className={cx("title")}>{colTitle}</h4>
                            {buttonLabel &&
                                !!orderedSpecs?.length &&
                                validSpecsCount < data[modalRow].length + 1 && (
                                    <Button
                                        onClick={() =>
                                            publish(EventNames.OpenSidebar, {
                                                sidebar: featuresSpecsModal,
                                                params: sidebarParams,
                                            })
                                        }
                                        theme="textLink"
                                        ariaLabel={buttonLabel}
                                    >
                                        {buttonLabel}
                                    </Button>
                                )}
                        </div>
                        {recentServiceDate && recentServiceMileage && (
                            <div className={cx("mostRecentServiceContainer")}>
                                <div className={cx("lastServiceDate", "row")}>
                                    <span className={cx("text", "key")}>Last service date</span>
                                    <span className={cx("value")}>{recentServiceDate}</span>
                                </div>
                                <div
                                    className={cx(
                                        "lastServiceMileage",
                                        "row",
                                        orderedSpecs.length >= 1
                                            ? "lessBottomPadding"
                                            : "bottomPadding"
                                    )}
                                >
                                    <span className={cx("text", "key")}>
                                        Mileage at last service
                                    </span>
                                    <span className={cx("value")}>{recentServiceMileage}</span>
                                </div>
                            </div>
                        )}
                        {orderedSpecs.length >= 1 && <div className={cx("hsep")} />}

                        {recentServiceDate && recentServiceMileage ? (
                            orderedSpecs
                        ) : (
                            <div
                                className={cx("noDataMessage")}
                                dangerouslySetInnerHTML={{
                                    __html: serviceHistory.noDataText,
                                }}
                            ></div>
                        )}
                        {serviceHistoryRequiresExpansionButton && (
                            <Button
                                onClick={() =>
                                    publish(EventNames.OpenSidebar, {
                                        sidebar: featuresSpecsModal,
                                        params: sidebarParams,
                                    })
                                }
                                theme="textLink"
                                ariaLabel={buttonLabel}
                                className={cx("seeMore")}
                            >
                                {`+ ${totalRemovedValues} more`}
                            </Button>
                        )}
                    </div>
                    {!lastCol && <div className={cx("sep")} />}
                </React.Fragment>
            ) : (
                <React.Fragment key={colTitle}>
                    <div className={cx("col")}>
                        <div className={cx("titleRow")}>
                            <h4 className={cx("title")}>{colTitle}</h4>
                            {buttonLabel &&
                                !!orderedSpecs?.length &&
                                validSpecsCount < data[modalRow].length && (
                                    <Button
                                        onClick={() =>
                                            publish(EventNames.OpenSidebar, {
                                                sidebar: featuresSpecsModal,
                                                params: sidebarParams,
                                            })
                                        }
                                        theme="textLink"
                                        ariaLabel={buttonLabel}
                                    >
                                        {buttonLabel}
                                    </Button>
                                )}
                        </div>
                        {orderedSpecs.length > 0 ? orderedSpecs : ""}
                        {!!motOrderedSpecs?.length && (
                            <>
                                <div className={cx("hsep")} />
                                {motOrderedSpecs}
                            </>
                        )}
                    </div>
                    {!lastCol && <div className={cx("sep")} />}
                </React.Fragment>
            );
        }
    );

    return (
        <div className={cx("vehicleFigures")}>
            {cols}
            <div className={cx("legal")}>
                <RichText {...components.legal} />
            </div>
        </div>
    );
};
